import { useMemo } from "react";
import {
  FloorPlanPanelWrapper,
  type ViewerMode,
} from "../ReviewPage/FloorPlanPanelWrapper";
import { ObjectNavigationBar } from "../ReviewPage/ObjectNavigationBar";
import { getPreviewUrlKey } from "../shared/getPreviewUrlKey";
import type { BoardItemOccurrenceV3 } from "./utils/types";

interface DiagramViewerV3Props {
  diagramName: string;
  selectedOccurrence: BoardItemOccurrenceV3 | null;
  canGoPrev: boolean;
  canGoNext: boolean;
  goPrev: () => void;
  goNext: () => void;
  goToIndex: (index: number) => void;
  selectedIndex: number;
  total: number;
  viewerMode: ViewerMode;
  onViewerModeChange: (mode: ViewerMode) => void;
}

const EMPTY_ARRAY: never[] = [];
const NOOP = () => {};

export const DiagramViewerV3 = ({
  diagramName,
  selectedOccurrence,
  canGoPrev,
  canGoNext,
  goPrev,
  goNext,
  goToIndex,
  selectedIndex,
  total,
  viewerMode,
  onViewerModeChange,
}: DiagramViewerV3Props) => {
  const pageIndex = useMemo(() => {
    if (!selectedOccurrence) return 0;
    const match = selectedOccurrence.page.match(/page_(\d+)/);
    return match ? parseInt(match[1], 10) : 0;
  }, [selectedOccurrence]);

  const plan = useMemo(
    () => ({
      previewUrl: selectedOccurrence?.imageUrl ?? "",
      pdfUrl: selectedOccurrence?.pdfUrl ?? "",
      pageNumber: pageIndex + 1,
      name: `${diagramName} / Page: ${pageIndex + 1}`,
      imageWidthPx: selectedOccurrence?.widthPx ?? 0,
      imageHeightPx: selectedOccurrence?.heightPx ?? 0,
    }),
    [selectedOccurrence, pageIndex, diagramName],
  );

  const imageScaleFactor = selectedOccurrence?.imageScaleFactor ?? 1;

  return (
    <div className="relative h-full">
      <FloorPlanPanelWrapper
        key={getPreviewUrlKey(plan.previewUrl)}
        plan={plan}
        viewerMode={viewerMode}
        onViewerModeChange={onViewerModeChange}
        imageScaleFactor={imageScaleFactor}
        detectedObjects={EMPTY_ARRAY}
        setSelectedObject={NOOP}
        zoomToFitOnImageLoad="always"
        isAutoZoomEnabled={false}
        showObjectNavigation={false}
      />
      {total > 1 && (
        <ObjectNavigationBar
          canPrev={canGoPrev}
          canNext={canGoNext}
          onPrev={goPrev}
          onNext={goNext}
          onSelectIndex={goToIndex}
          selectedIndex={selectedIndex}
          total={total}
        />
      )}
    </div>
  );
};
